import { useEffect, useState } from 'react';
import { apiClient } from '../network/ApiClient';
import { getCurrentLocale, setLanguage } from '../i18n';
import type { AppLocale } from '../i18n/locale';
import { uiKey, type UiMessage } from '../i18n/uiMessage';
import { useAuth } from '../context/AuthContext';
import { useIdentity, patchIdentity } from './useIdentity';

/**
 * Account-level language preference (Localization Phase 10) — the single
 * authenticated ownership seam between the device-local i18n layer and the
 * backend account preference.
 *
 * Responsibilities (and the ONLY module with them):
 *   • APPLY: once per authenticated identity, when /profile carries a valid
 *     preferred_locale that differs from the active language, apply it
 *     (account is authoritative when it exists).
 *   • BOOTSTRAP: once per authenticated identity, when the account carries
 *     null (never chose anywhere), persist the current device-resolved
 *     locale in the background — no visible switch, failure never blocks or
 *     loops (the once-mark is set before the request fires).
 *   • SAVE: explicit authenticated selections switch optimistically and are
 *     persisted through the serialized write queue below.
 *
 * PERSISTED last-choice-wins — the write queue:
 * At most ONE locale PATCH is ever in flight per identity. A selection made
 * while a request is in flight only records the newest desired locale;
 * intermediate choices are coalesced away. When the in-flight request
 * settles, the queue persists the newest desired locale — unless it already
 * equals the last server-confirmed value, in which case no redundant PATCH
 * is sent. Because requests are strictly serialized, the final deliberate
 * choice is always the final PATCH the server receives, and therefore the
 * final database value — this cannot be guaranteed by response-epochs or
 * aborts alone, since a stale request may already have reached the server.
 *
 * Failure semantics: a stale/intermediate failure never rolls the UI back
 * (a newer desired choice exists and is persisted next); only a failure of
 * the FINAL desired locale rolls back — to the last server-confirmed locale,
 * or to the locale active before the first explicit selection when the
 * account had no confirmed preference. No automatic retry follows a final
 * failure.
 *
 * Identity scoping: the queue is keyed to the authenticated identityKey.
 * Logout/account switch drops the queue (pending desires are never issued
 * under another account's credentials) and a request that settles after a
 * switch is ignored — it can never touch the new account's state.
 *
 * setLanguage() stays the low-level device operation: nothing here makes
 * ordinary programmatic language changes write to the backend. Only the
 * explicit save() and the one-time null bootstrap persist. Logged-out
 * selections never touch this module.
 */

export type LanguageSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface SaveState {
  status: LanguageSaveStatus;
  error:  UiMessage | null;
}

// ── Module state ─────────────────────────────────────────────────────────────

/** identityKeys whose apply-or-bootstrap decision already ran this app life.
 *  Cleared on logout so a later login of the same account safely re-syncs
 *  (apply is idempotent; bootstrap re-fires only if the account is still null). */
const syncedIdentityKeys = new Set<string>();

/** The serialized/coalescing write queue — one per authenticated identity. */
interface LocaleWriteQueue {
  /** The identity this queue belongs to. Settling requests compare against
   *  the CURRENT queue's key; a mismatch means logout/switch happened. */
  key: string;
  /** True while a PATCH is on the wire. Never two at once. */
  inFlight: boolean;
  /** Newest desired locale awaiting dispatch (coalesces intermediates). */
  pending: AppLocale | null;
  /** Last server-confirmed value (seeded from the loaded identity, then
   *  updated by successful PATCHes). null = account has no confirmed value. */
  confirmed: AppLocale | null;
  /** Rollback anchor for a final failure when confirmed === null: the locale
   *  active before the FIRST explicit selection of this queue's life. */
  preSelection: AppLocale | null;
  /** True once any explicit selection entered this queue — gates the
   *  visible saving/saved/error status (a passive bootstrap stays silent). */
  hadExplicit: boolean;
}

let queue: LocaleWriteQueue | null = null;

let saveState: SaveState = { status: 'idle', error: null };
const saveSubscribers = new Set<(s: SaveState) => void>();

function setSaveState(next: SaveState): void {
  saveState = next;
  for (const notify of saveSubscribers) notify(next);
}

/** Test-only: reset all module state between test cases. */
export function __resetAccountLanguageForTests(): void {
  syncedIdentityKeys.clear();
  queue = null;
  saveState = { status: 'idle', error: null };
}

function queueFor(key: string, confirmed: AppLocale | null): LocaleWriteQueue {
  if (!queue || queue.key !== key) {
    queue = { key, inFlight: false, pending: null, confirmed, preSelection: null, hadExplicit: false };
  }
  return queue;
}

/** Enqueue a persistence target. Serialized: dispatches immediately when the
 *  wire is free, otherwise just records the newest desire (coalescing). */
function enqueue(q: LocaleWriteQueue, target: AppLocale): void {
  q.pending = target;
  if (!q.inFlight) dispatchNext(q);
}

function dispatchNext(q: LocaleWriteQueue): void {
  const target = q.pending;
  q.pending = null;
  if (target === null) return;

  // Newest desire already matches the server → nothing to send. For an
  // explicit flow this IS success (e.g. the user returned to the original
  // locale while an intermediate request was failing/succeeding).
  if (target === q.confirmed) {
    if (q.hadExplicit) {
      patchIdentity({ preferredLocale: target, preferredLocaleUnsupported: false });
      setSaveState({ status: 'saved', error: null });
    }
    return;
  }

  q.inFlight = true;
  const dispatchedForKey = q.key;

  void apiClient
    .updateLocale(target)
    .then(res => settle(dispatchedForKey, target, res))
    .catch(() => settle(dispatchedForKey, target, { ok: false as const, error: 'unknown', message: '' }));
}

function settle(
  dispatchedForKey: string,
  target: AppLocale,
  res: { ok: true; preferred_locale: string | null } | { ok: false; error: string; message: string; code?: string },
): void {
  // Logout or account switch while on the wire: the request may well have
  // reached the server under the OLD account's credentials (that account
  // keeps the stored value for its next login), but it must not touch the
  // current queue, identity, UI or status — and any old pending desire was
  // dropped with the old queue, so it is never issued under new credentials.
  if (!queue || queue.key !== dispatchedForKey) return;
  const q = queue;
  q.inFlight = false;

  if (res.ok) q.confirmed = target;

  // A newer desired choice exists → this settle is STALE. Its success just
  // updated `confirmed`; its failure must not roll anything back. Either
  // way, continue by persisting the newest desire.
  if (q.pending !== null) {
    dispatchNext(q);
    return;
  }

  // This was the FINAL desired locale.
  if (res.ok) {
    if (q.hadExplicit) {
      patchIdentity({ preferredLocale: target, preferredLocaleUnsupported: false });
      setSaveState({ status: 'saved', error: null });
    } else {
      // Passive bootstrap success — identity reflects it, no status noise.
      patchIdentity({ preferredLocale: target, preferredLocaleUnsupported: false });
    }
    return;
  }

  // Final failure.
  if (!q.hadExplicit) return; // passive bootstrap failure stays silent, never retries

  const rollbackTo = q.confirmed ?? q.preSelection;
  if (rollbackTo && rollbackTo !== getCurrentLocale()) {
    void setLanguage(rollbackTo); // restores i18next + <html lang> + localStorage together
  }
  // A backend-coded rejection (e.g. invalid_locale) renders through the
  // shared code→catalog boundary so it relabels EN→ES→EN on a language
  // switch without resubmitting; an uncoded failure keeps the legacy raw
  // (untranslatable backend prose) or frontend-owned fallback behavior.
  const error: UiMessage = res.code
    ? { kind: 'code', code: res.code, fallback: res.message || undefined }
    : res.message
      ? { kind: 'raw', text: res.message }
      : uiKey('language.saveError');
  setSaveState({ status: 'error', error });
}

// ── Apply / bootstrap sync (mounted once, in AppShell) ───────────────────────

/**
 * Mount ONCE in the app shell. Watches the authenticated identity and runs
 * the apply-or-bootstrap decision exactly once per identityKey. Cleans its
 * once-marks, queue and save status on logout, so account switching can
 * never leak one account's state into the next and a later login re-syncs.
 */
export function useAccountLanguageSync(): void {
  const { isLoggedIn, identityKey } = useAuth();
  const identity = useIdentity();

  useEffect(() => {
    if (!isLoggedIn || !identityKey) {
      // Logout / pre-auth: forget the once-marks (a later login re-syncs),
      // drop the queue (pending desires are never issued under another
      // account's credentials) and clear any lingering save status. The
      // device language itself is deliberately untouched — logging out
      // never resets the UI language.
      syncedIdentityKeys.clear();
      queue = null;
      if (saveState.status !== 'idle') setSaveState({ status: 'idle', error: null });
      return;
    }
    if (!identity) return;                       // identity still loading
    if (syncedIdentityKeys.has(identityKey)) return;
    syncedIdentityKeys.add(identityKey);         // once per identity — set BEFORE any async work

    // Seed (or re-key) the queue with this account's server-confirmed value.
    const q = queueFor(identityKey, identity.preferredLocale);

    if (identity.preferredLocale) {
      // Account preference exists → authoritative. setLanguage also updates
      // <html lang> and localStorage, so this device follows the account.
      // No write back to the server — it already holds this value.
      if (identity.preferredLocale !== getCurrentLocale()) {
        void setLanguage(identity.preferredLocale);
      }
    } else if (!identity.preferredLocaleUnsupported) {
      // Account never chose anywhere → keep the device-resolved language and
      // persist it once in the background THROUGH THE SAME QUEUE as explicit
      // saves (so an explicit choice made while the bootstrap is in flight
      // is simply the queue's next — and final — write). Failure is silent
      // and terminal for this login (mark already set — no retry loop);
      // a later login retries while the account remains null. An UNSUPPORTED
      // stored value (future locale from a newer client) is deliberately NOT
      // overwritten.
      enqueue(q, getCurrentLocale());
    }
  }, [isLoggedIn, identityKey, identity]);
}

// ── Explicit authenticated save ──────────────────────────────────────────────

/**
 * Explicit authenticated language selection: optimistic switch immediately,
 * then persistence via the serialized queue. Selecting the already-confirmed
 * locale with a free wire sends nothing. Returns after the optimistic
 * switch — persistence outcome is delivered through useLanguageSaveStatus.
 */
export async function saveAccountLanguage(next: AppLocale): Promise<void> {
  if (!queue) {
    // No authenticated queue (identity not yet synced) — behave device-only;
    // the sync effect will reconcile when identity arrives.
    await setLanguage(next);
    return;
  }
  const q = queue;
  if (!q.hadExplicit) {
    q.hadExplicit = true;
    if (q.confirmed === null) q.preSelection = getCurrentLocale();
  }

  await setLanguage(next);                       // optimistic: UI + <html lang> + localStorage

  // Nothing to persist and nothing on the wire → already saved.
  if (!q.inFlight && q.pending === null && q.confirmed === next) {
    setSaveState({ status: 'saved', error: null });
    return;
  }

  setSaveState({ status: 'saving', error: null });
  enqueue(q, next);
}

/** Subscribe to the save status (selector UI). */
export function useLanguageSaveStatus(): SaveState {
  const [state, setState] = useState<SaveState>(saveState);
  useEffect(() => {
    saveSubscribers.add(setState);
    return () => { saveSubscribers.delete(setState); };
  }, []);
  return state;
}
